import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import {
  Space,
  Form,
  Select as AntSelect,
  InputNumber,
  Button as AntButton
} from 'antd';

import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import Icon from 'components/Icon';
import Input from 'components/Input';
import {
  CurrencyCode,
  HiddenContainer,
  HideContainer,
  MinimumPriceContainer,
  MinimumPriceListContainer,
  ValueContainer,
  InputContainer
} from 'modules/OnlineSales/components/MinimumPrice/components';
import Button from 'components/Button';
import Popup from 'components/Popup';

import { getPmcs } from 'actions/onlineSales';

const MinimumPrice = ({
  wine,
  vintageOptions,
  currencyOptions,
  savePmcs,
  isAdmin
}) => {
  const [form] = Form.useForm();
  const [isVisible, setIsVisible] = useState(true);
  const [load, setLoad] = useState(false);

  const dispatch = useDispatch();
  const data = useSelector((state) => state.onlineSales.pmcs)
    .filter((item) => item.wine === wine)
    .sort(
      (a, b) =>
        Number(a && a.vintage ? a.vintage : 0) -
        Number(b && b.vintage ? b.vintage : 0)
    );

  const [pmcs, setPmcs] = useState([]);

  useEffect(() => {
    dispatch(getPmcs());
  }, []);

  useEffect(() => {
    if (JSON.stringify(data) != JSON.stringify(pmcs)) {
      setPmcs(data);
    }
  }, [JSON.stringify(data)]);

  const onCardHide = useCallback(() => {
    setIsVisible(!isVisible);
  }, [isVisible]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handlePmcChange = (pmm, value) => {
    if (!isNaN(value) && Number(value) >= 0) {
      const newPmcs = [...pmcs].map((item) =>
        item.id === pmm.id ? { ...pmm, price: Number(value) } : item
      );
      setPmcs(newPmcs);
    }
  };

  const onClosePopUp = () => {
    setIsModalOpen(false);
  };

  const onSave = (values) => {
    setLoad(true);
    savePmcs([
      {
        wine,
        vintage: values.vintage,
        currency: values.currency,
        price: values.price
      }
    ]);
    setLoad(false);
    onClosePopUp();
    form.resetFields();
  };

  return isVisible ? (
    <MinimumPriceContainer>
      <Card>
        <CardHeader
          title={I18n.t('onlineSales.pmm.title')}
          fontSize="20px"
          alignItems="center"
          rightComponents={
            <HideContainer onClick={onCardHide}>
              <Icon icon="eyeClosed" />
              <p>{I18n.t('onlineSales.pmm.hideLabel')}</p>
            </HideContainer>
          }
        />
        <MinimumPriceListContainer>
          {pmcs.map((pmm, index) => (
            <ValueContainer key={index}>
              <CurrencyCode>{pmm.vintage}</CurrencyCode>
              <InputContainer
                length={pmm.price ? String(pmm.price).length : 0}
                lengthCurrency={
                  Number(null)
                    .toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: pmm.currency || 'EUR',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })
                    .replace('0', '').length
                }
              >
                {isAdmin ? (
                  <React.Fragment>
                    <Input
                      value={pmm.price}
                      placeholder="-"
                      onChange={(value) => handlePmcChange(pmm, value)}
                      styles={`input{height:22px;background-color:transparent;border:0;font-size:12px;padding:0 !important;width:100% !important;`}
                    />
                    <p>
                      {Number(null)
                        .toLocaleString('fr-FR', {
                          style: 'currency',
                          currency: pmm.currency,
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })
                        .replace('0', '')}
                    </p>
                  </React.Fragment>
                ) : (
                  <p>
                    {pmm.price
                      ? pmm.price.toLocaleString('fr-FR', {
                          style: 'currency',
                          currency: pmm.currency,
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })
                      : '-'}
                  </p>
                )}
              </InputContainer>
            </ValueContainer>
          ))}
        </MinimumPriceListContainer>
        <Popup
          open={isModalOpen}
          title={I18n.t('onlineSales.addPmc')}
          icon="plus"
          hideButton
          onClose={onClosePopUp}
        >
          <div className="modalContent">
            <Form
              layout="vertical"
              name="validateOnly"
              form={form}
              onFinish={onSave}
              requiredMark={false}
            >
              <Form.Item
                label={I18n.t('onlineSales.vintageLabel')}
                name="vintage"
                rules={[
                  {
                    required: true,
                    message: I18n.t('onlineSales.requiredForm')
                  }
                ]}
              >
                <AntSelect
                  size="large"
                  placeholder={I18n.t('onlineSales.vintagePlaceholder')}
                  options={vintageOptions}
                />
              </Form.Item>
              <Form.Item
                label={I18n.t('onlineSales.currencyLabel')}
                name="currency"
                rules={[
                  {
                    required: true,
                    message: I18n.t('onlineSales.requiredForm')
                  }
                ]}
              >
                <AntSelect
                  size="large"
                  placeholder={I18n.t('onlineSales.currencyPlaceholder')}
                  options={currencyOptions}
                />
              </Form.Item>
              <Form.Item
                label={I18n.t('onlineSales.priceLabel')}
                name="price"
                rules={[
                  {
                    required: true,
                    message: I18n.t('onlineSales.requiredForm')
                  }
                ]}
              >
                <InputNumber
                  min={0}
                  size="large"
                  placeholder={I18n.t('onlineSales.pricePlaceholder')}
                  formatter={(value) => `${value.replace('.', '')}`}
                  style={{
                    width: '100%'
                  }}
                />
              </Form.Item>
              <Form.Item style={{ paddingTop: 20 }}>
                <Space
                  size="large"
                  style={{ justifyContent: 'end', width: '100%' }}
                >
                  <AntButton size="large" onClick={onClosePopUp}>
                    Annuler
                  </AntButton>
                  <AntButton
                    type="primary"
                    size="large"
                    loading={load}
                    htmlType="submit"
                  >
                    Valider
                  </AntButton>
                </Space>
              </Form.Item>
            </Form>
          </div>
        </Popup>
        {isAdmin && (
          <div className="pmcsButtons">
            <Button
              label={I18n.t('onlineSales.addPmc')}
              width="160px"
              color="dark"
              iconLeft
              fontFamily="SFCompactText"
              height="32px"
              textTransform="normal"
              borderRadius="24px"
              fontSize="12px"
              onClick={() => setIsModalOpen(true)}
            />
            <Button
              label={I18n.t('onlineSales.savePmc')}
              width="125px"
              iconLeft
              fontFamily="SFCompactText"
              height="32px"
              textTransform="normal"
              borderRadius="24px"
              fontSize="12px"
              onClick={() => savePmcs(pmcs)}
            />
          </div>
        )}
      </Card>
    </MinimumPriceContainer>
  ) : (
    <MinimumPriceContainer hidden={true}>
      <HiddenContainer onClick={onCardHide}>
        <Icon icon="eye2" />
        <p>{I18n.t('onlineSales.pmm.showLabel')}</p>
      </HiddenContainer>
    </MinimumPriceContainer>
  );
};

export default MinimumPrice;
